import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, ArrowRight } from "lucide-react";

const TEMPLATE_GLYPH = { "static-site": "🌐", "vite-react": "⚛️", "fastapi-app": "🐍" };

export function CreateProjectModal({ onClose, onCreated }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [project_type, setType] = useState("web");
  const [layout_template, setLayoutTemplate] = useState("flat");
  const [repo_url, setRepo] = useState("");
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(""); // "" = Blank
  const colors = ["#2563EB", "#059669", "#D97706", "#DC2626", "#7C3AED"];
  const [color, setColor] = useState(colors[0]);

  useEffect(() => {
    api.get("/templates", { skipErrorToast: true })
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => setTemplates([])); // gallery unavailable -> Blank-only, still usable
  }, []);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/projects",
        { name, description, project_type, repo_url, color, layout_template, ...(template ? { template } : {}) },
        { skipErrorToast: true });
      if (data.scaffold_pending) {
        toast.info("Project created — pair a host to scaffold the template");
      } else if (data.scaffold && !data.scaffold.ok) {
        toast.error(`Template scaffold failed: ${data.scaffold.error}`);
      }
      onCreated(data.project);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(formatApiError(e.response?.data?.detail), {
          action: { label: "Upgrade", onClick: () => navigate("/billing") },
        });
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-700 text-lg">New project</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="new-project-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
          <textarea data-testid="new-project-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          <input data-testid="new-project-repo" value={repo_url} onChange={(e) => setRepo(e.target.value)} placeholder="GitHub repo URL (optional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
          <div>
            <label className="text-xs font-600 text-gray-500 mb-1.5 block">Start from</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="template-card-blank" onClick={() => setTemplate("")}
                className={`text-left border rounded-lg p-2.5 transition-colors ${template === "" ? "border-gray-900 ring-1 ring-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-400"}`}>
                <span className="text-base leading-none">✦</span>
                <div className="text-[13px] font-600 text-gray-900 mt-1">Blank</div>
                <div className="text-[11px] text-gray-500 leading-snug">Empty project — bring your own files.</div>
              </button>
              {templates.map((t) => (
                <button type="button" key={t.id} data-testid={`template-card-${t.id}`} onClick={() => setTemplate(t.id)}
                  className={`text-left border rounded-lg p-2.5 transition-colors ${template === t.id ? "border-gray-900 ring-1 ring-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-400"}`}>
                  <span className="text-base leading-none">{TEMPLATE_GLYPH[t.id] || "📦"}</span>
                  <div className="text-[13px] font-600 text-gray-900 mt-1">{t.name}</div>
                  <div className="text-[11px] text-gray-500 leading-snug">{t.description}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select data-testid="new-project-type" value={project_type} onChange={(e) => setType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
              <option value="web">Web</option><option value="node">Node</option><option value="python">Python</option><option value="other">Other</option>
            </select>
            <div className="flex items-center gap-1.5">
              {colors.map((c) => (
                <button key={c} onClick={() => setColor(c)} style={{ background: c }}
                  className={`w-6 h-6 rounded-full ${color === c ? "ring-2 ring-offset-2 ring-gray-900" : ""}`} />
              ))}
            </div>
          </div>
          {template === "" ? (
            <div>
              <label className="text-xs font-600 text-gray-500 mb-1.5 block">Project structure</label>
              <select data-testid="new-project-layout" value={layout_template} onChange={(e) => setLayoutTemplate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                <option value="flat">Flat — single tree (static / scripts)</option>
                <option value="fullstack">Full-stack — frontend/ + backend/</option>
                <option value="node_web">Node web — src/ + public/</option>
                <option value="python_service">Python service — app/ + tests/</option>
                <option value="monorepo">Monorepo — apps/ + packages/</option>
              </select>
              <p className="text-[11px] text-gray-400 mt-1">The planner organizes generated code into these folders and flags work that drifts outside them.</p>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400">Project structure and dev-server command come from the template.</p>
          )}
        </div>
        <button data-testid="new-project-submit" disabled={busy || !name.trim()} onClick={create}
          className="mt-5 w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create & open <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}
